import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import type { ExerciseCatalogEntry, User } from '../types';
import { findCatalogMatch, normalizeExerciseName } from '../utils/exerciseCatalog';
import { OFFLINE_CACHE_KEYS, readCache, type LoadSource, writeCache } from '../utils/offlineCache';

export function useExerciseCatalog(user: User | null) {
  const [exerciseCatalog, setExerciseCatalog] = useState<ExerciseCatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const loadCatalog = useCallback(async (): Promise<LoadSource> => {
    if (!user) return 'empty';
    setCatalogLoading(true);
    try {
      const { data, error } = await supabase
        .from('exercise_catalog')
        .select('id,name,normalized_name,owner_user_id,created_at,primary_muscles,secondary_muscles,equipment')
        .or(`owner_user_id.eq.${user.id},owner_user_id.is.null`)
        .order('name', { ascending: true });

      if (error) throw error;
      const nextCatalog = data || [];
      setExerciseCatalog(nextCatalog);
      await writeCache(OFFLINE_CACHE_KEYS.catalog(user.id), nextCatalog, user.id);
      return 'network';
    } catch (error) {
      console.error('Error cargando catálogo:', error);
      const cached = await readCache<ExerciseCatalogEntry[]>(OFFLINE_CACHE_KEYS.catalog(user.id), user.id);
      if (cached) {
        setExerciseCatalog(cached);
        return 'cache';
      }
      return 'empty';
    } finally {
      setCatalogLoading(false);
    }
  }, [user]);

  const refreshExerciseCatalog = useCallback(async () => {
    if (!user) return [];
    const { data, error } = await supabase
      .from('exercise_catalog')
      .select('id,name,normalized_name,owner_user_id,created_at,primary_muscles,secondary_muscles,equipment')
      .or(`owner_user_id.eq.${user.id},owner_user_id.is.null`)
      .order('name', { ascending: true });

    if (error) throw error;
    setExerciseCatalog(data || []);
    return data || [];
  }, [user]);

  const ensureExerciseEntry = useCallback(
    async (name: string): Promise<ExerciseCatalogEntry> => {
      if (!user) throw new Error('Usuario no disponible');

      const trimmedName = name.trim();
      if (!trimmedName) throw new Error('El nombre del ejercicio es obligatorio');

      const existing = findCatalogMatch(exerciseCatalog, trimmedName, user.id);
      if (existing) return existing;

      const normalizedName = normalizeExerciseName(trimmedName);
      const { data, error } = await supabase
        .from('exercise_catalog')
        .insert({
          name: trimmedName,
          normalized_name: normalizedName,
          owner_user_id: user.id,
        })
        .select('id,name,normalized_name,owner_user_id,created_at,primary_muscles,secondary_muscles,equipment')
        .single();

      if (!error && data) {
        setExerciseCatalog((prev) =>
          [...prev, data].sort((a, b) => a.name.localeCompare(b.name))
        );
        return data;
      }

      const refreshedCatalog = await refreshExerciseCatalog();
      const refreshedMatch = findCatalogMatch(
        refreshedCatalog,
        trimmedName,
        user.id
      );
      if (refreshedMatch) return refreshedMatch;

      throw error || new Error('No se pudo crear el ejercicio');
    },
    [exerciseCatalog, refreshExerciseCatalog, user]
  );

  return {
    exerciseCatalog,
    catalogLoading,
    loadCatalog,
    ensureExerciseEntry,
    setExerciseCatalog,
  };
}
